import { editorUrl } from '@/lib/editorBasePath';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Loader2, Heart, ChevronDown, ChevronRight, BookOpen, Upload } from 'lucide-react';
import { CoverInfosByStyle } from '@/hooks/useBookLoader';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute('content') ?? '';
}

interface CoverItem {
  id: number;
  preview: string;
  name: string;
  is_favoris?: boolean;
}

type CoversByCategory = Record<string, CoverItem[]>;

interface CoverPanelProps {
  onApplyCover: (data: { elements_front: any[]; elements_back: any[]; cover_name?: string }) => void;
  coverInfos: CoverInfosByStyle;
  isWememberz?: boolean;
  /** Apply (and save) the first cover of the list automatically when the book has no cover yet. */
  autoApplyDefault?: boolean;
}

const CoverPanel: React.FC<CoverPanelProps> = ({ onApplyCover, coverInfos, isWememberz = false, autoApplyDefault = false }) => {
  const [coversByCategory, setCoversByCategory] = useState<CoversByCategory | null>(null);
  const [myCovers, setMyCovers] = useState<CoverItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [applying, setApplying] = useState<number | null>(null);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCustomCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('background_type', 'cover');
      const res = await fetch(editorUrl('/add_custom_trame_background'), {
        method: 'POST',
        headers: { 'X-CSRF-Token': getCsrfToken() },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      setReloadKey((k) => k + 1);
    } catch (err) {
      console.warn('[CoverPanel] Custom cover upload failed:', err);
    } finally {
      setUploading(false);
      setShowCustomDialog(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    setLoading(true);
    fetch(editorUrl('/load_covers'))
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const cats: CoversByCategory = data.covers_by_category ?? {};
        setCoversByCategory(cats);
        setMyCovers(data.my_covers ?? []);
        const favSet = new Set<number>();
        for (const items of Object.values(cats)) {
          for (const item of items) {
            if (item.is_favoris) favSet.add(item.id);
          }
        }
        setFavorites(favSet);
      })
      .catch((err) => console.warn('[CoverPanel] Failed to load covers:', err))
      .finally(() => setLoading(false));
  }, [reloadKey]);

  const handleClick = (coverId: number, isCustom = false) => {
    setApplying(coverId);
    const payload: Record<string, any> = isCustom
      ? { background_id: coverId, custom_background: true }
      : { cover_id: coverId };
    fetch(editorUrl('/save_covers'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        console.log('[CoverPanel] save_covers response:', JSON.stringify(data).slice(0, 500));
        const cover = data.cover ?? data;
        const coverName = cover.name ?? cover.cover_name ?? data.cover_name ?? '';
        onApplyCover({
          elements_front: cover.elements_front ?? [],
          elements_back: cover.elements_back ?? [],
          cover_name: coverName,
        });
      })
      .catch((err) => console.warn('[CoverPanel] Failed to apply cover:', err))
      .finally(() => setApplying(null));
  };

  // Book without any cover: apply + save the very first cover of the list.
  const autoAppliedRef = useRef(false);
  useEffect(() => {
    if (!autoApplyDefault || autoAppliedRef.current || loading || !coversByCategory) return;
    const firstCategory = Object.values(coversByCategory).find((items) => items && items.length > 0);
    const firstCover = firstCategory?.[0];
    if (!firstCover) return;
    autoAppliedRef.current = true;
    handleClick(firstCover.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoApplyDefault, loading, coversByCategory]);

  const toggleFavorite = useCallback((e: React.MouseEvent, coverId: number) => {
    e.stopPropagation();
    const isFav = favorites.has(coverId);
    const endpoint = isFav ? '/editor/remove_cover_to_favori' : '/editor/add_cover_to_favori';

    setFavorites((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(coverId);
      else next.add(coverId);
      return next;
    });

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ background_id: coverId }),
    }).catch(() => {
      setFavorites((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(coverId);
        else next.delete(coverId);
        return next;
      });
    });
  }, [favorites]);

  const toggleCategory = (key: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderCoverItem = (cover: CoverItem, showFav = true, isCustom = false) => (
    <div key={cover.id} className="relative group">
      <button
        onClick={() => handleClick(cover.id, isCustom)}
        disabled={applying !== null}
        className={`w-full rounded-lg overflow-hidden border transition-colors relative ${
          applying === cover.id
            ? 'border-editor-sidebar-active opacity-70'
            : 'border-editor-sidebar-border hover:border-editor-sidebar-active'
        }`}
      >
        {applying === cover.id && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-10">
            <Loader2 size={20} className="animate-spin text-white" />
          </div>
        )}
        <img
          src={cover.preview}
          alt={cover.name}
          className="w-full h-auto object-contain"
          loading="lazy"
        />
      </button>
      {showFav && (
        <button
          onClick={(e) => toggleFavorite(e, cover.id)}
          className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/40 hover:bg-black/60 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
          title={favorites.has(cover.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        >
          <Heart
            size={12}
            className={favorites.has(cover.id) ? 'text-red-500 fill-red-500' : 'text-white'}
          />
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={20} className="animate-spin text-editor-sidebar-foreground" />
      </div>
    );
  }

  const sortedKeys = coversByCategory ? Object.keys(coversByCategory).sort((a, b) => Number(a) - Number(b)) : [];

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-editor-sidebar-foreground text-center flex items-center justify-center gap-1.5">
        <BookOpen size={15} />
        Couverture
      </h3>

      {/* Custom cover upload button */}
      {!isWememberz && (
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2 text-xs"
          onClick={() => setShowCustomDialog(true)}
        >
          <Upload size={14} />
          Ajouter ma propre couverture
        </Button>
      )}

      {/* Custom cover upload dialog */}
      <Dialog open={showCustomDialog} onOpenChange={setShowCustomDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-center">
              Ajouter ma propre couverture
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              *La couverture doit mesurer 430×307 mm. En JPG ou PNG avec une résolution de 300 dpi. Il s'agit d'un format A3 paysage avec 5 mm de fond perdu nécessaires pour l'impression.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              className="hidden"
              onChange={handleCustomCoverUpload}
            />
            <Button
              className="w-full gap-2"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Upload size={16} />
              )}
              {uploading ? 'Envoi en cours…' : 'Choisir un fichier'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-1.5">
        {!isWememberz && (
          <span className="text-[10px] px-2 py-1 rounded-full border bg-editor-sidebar-active text-white border-editor-sidebar-active cursor-default">
            📁 Mes couvertures
          </span>
        )}
        <span className="text-[10px] px-2 py-1 rounded-full border bg-editor-sidebar-active text-white border-editor-sidebar-active cursor-default">
          ❤️ Favoris
        </span>
        {sortedKeys.map((key) => {
          const info = coverInfos[key];
          if (!info) return null;
          const isOpen = !collapsedCategories.has(key);
          return (
            <button
              key={key}
              onClick={() => toggleCategory(key)}
              className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                isOpen
                  ? 'bg-editor-sidebar-active text-white border-editor-sidebar-active'
                  : 'bg-transparent text-editor-sidebar-foreground border-editor-sidebar-border hover:border-editor-sidebar-active'
              }`}
            >
              {info.name}
            </button>
          );
        })}
      </div>

      {/* Mes couvertures – always open */}
      {!isWememberz && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground">📁 Mes couvertures</h4>
          {myCovers.length === 0 ? (
            <p className="text-[10px] text-muted-foreground italic">Aucune couverture personnalisée.</p>
          ) : (
            <div className="space-y-2">
              {myCovers.map((cover) => renderCoverItem(cover, false, true))}
            </div>
          )}
        </div>
      )}

      {/* Favoris – always open */}
      {(() => {
        const favItems = sortedKeys.flatMap((key) =>
          (coversByCategory?.[key] || []).filter((item) => favorites.has(item.id))
        );
        return (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground">❤️ Favoris</h4>
            {favItems.length === 0 ? (
              <p className="text-[10px] text-muted-foreground italic">Aucun favori pour le moment.</p>
            ) : (
              <div className="space-y-2">
                {favItems.map((item) => renderCoverItem(item))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Category sections – open by default */}
      {sortedKeys.map((key) => {
        const items = coversByCategory?.[key];
        if (!items || items.length === 0) return null;
        const info = coverInfos[key];
        const categoryName = info?.name || key;
        const isCollapsed = collapsedCategories.has(key);

        return (
          <div key={key} className="space-y-2">
            <button
              onClick={() => toggleCategory(key)}
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-editor-sidebar-foreground transition-colors w-full"
            >
              {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
              <span className="capitalize">{categoryName}</span>
            </button>
            {!isCollapsed && (
              <div className="space-y-2">
                {items.map((item) => renderCoverItem(item))}
              </div>
            )}
          </div>
        );
      })}

      {!coversByCategory && sortedKeys.length === 0 && myCovers.length === 0 && (
        <p className="text-xs text-muted-foreground">Aucun modèle de couverture disponible.</p>
      )}
    </div>
  );
};

export default CoverPanel;
